// PaymentPage.tsx
import React from "react";
import { useLocation } from "react-router-dom";

type PrefillState = {
  amountINR?: number;
  amountUSD?: number;
  role?: string;
  name?: string;
  email?: string;
};

export default function PaymentPage(): JSX.Element {
  const { state } = useLocation();
  const s = (state || {}) as PrefillState;

  const defaultINR = s.amountINR ?? 1150;
  const defaultUSD = s.amountUSD ?? 15;
  const [amountINR] = React.useState<number>(defaultINR);
  const [amountUSD] = React.useState<number>(defaultUSD);

  const PAYPAL_LINK = "https://www.paypal.com/ncp/payment/5XSS7ZFGKUQP4";
  const PAYPAL_EMAIL = "technocollabs@gmail.com";
  const PAYPAL_NAME = "TECHNOCOLABS SOFTWARE PRIVATE LIMITED";
  const UPI_ID = "8319291391@okbizaxis";
  const BANK = {
    name: "HDFC Bank",
    accountName: "TECHNOCOLABS SOFTWARE PRIVATE LIMITED",
    accountNo: "50200052914042",
    ifsc: "HDFC0000281",
    type: "CURRENT ACCOUNT",
  };

  // icon URLs
  const ICON_GPAY = "https://img.icons8.com/color/48/google-pay--v1.png";
  const ICON_PHONEPE = "https://img.icons8.com/color/48/phone-pe.png";
  const ICON_PAYTM = "https://img.icons8.com/color/48/paytm.png";
  const ICON_CRED = "https://cdn.brandfetch.io/id27bJP5LK/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1668516348931";
  const ICON_BHIM = "https://img.icons8.com/color/48/bhim.png";
  const PAYPAL_ICON = "https://img.icons8.com/ios/50/paypal.png";

  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<"paypal" | "upi" | "bank">("upi");
  const [leftQrError, setLeftQrError] = React.useState(false);
  const [rightQrError, setRightQrError] = React.useState(false);
  const [showLargeLeftQR, setShowLargeLeftQR] = React.useState(false);

  React.useEffect(() => {
    if (copiedKey) {
      const t = setTimeout(() => setCopiedKey(null), 2200);
      return () => clearTimeout(t);
    }
  }, [copiedKey]);

  async function copy(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
    } catch {
      const inp = document.createElement("input");
      document.body.appendChild(inp);
      (inp as HTMLInputElement).value = text;
      inp.select();
      document.execCommand("copy");
      inp.remove();
      setCopiedKey(key);
    }
  }

  function copyAllBank() {
    const all = `Bank: ${BANK.name}\nA/C: ${BANK.accountNo}\nA/C Name: ${BANK.accountName}\nIFSC: ${BANK.ifsc}\nType: ${BANK.type}`;
    copy(all, "bank-all");
  }

  const confirmNote = `After payment, please email payment screenshot to contact@technocolabs.com with your name and role (if any).`;
  const qrPath = `${window.location.origin}/QR-Code.png`;

  function buildUpiLink({
    vpa,
    name,
    note,
    amount,
  }: {
    vpa: string;
    name?: string;
    note?: string;
    amount?: number | string;
  }) {
    const params = new URLSearchParams();
    params.set("pa", vpa);
    if (name) params.set("pn", name);
    if (note) params.set("tn", note);
    if (amount !== undefined && amount !== null && String(amount) !== "") params.set("am", String(amount));
    params.set("cu", "INR");
    return `upi://pay?${params.toString()}`;
  }

  /**
   * Attempt to open a UPI app specifically:
   * - Try to use Android intent with the desired package (works in Chrome Android).
   * - Fallback to generic upi:// link.
   * - Final fallback: copy VPA and notify user.
   *
   * Package names used (common defaults):
   *  - Google Pay: com.google.android.apps.nbu.paisa.user
   *  - PhonePe: com.phonepe.app
   *  - Paytm: net.one97.paytm
   *  - BHIM: in.org.npci.upiapp
   *
   * NOTE: deep-link behavior depends on user's device/browser. iOS often doesn't allow intents;
   * in such cases the generic upi:// (or copy) is used.
   */
  function openUpiForApp(app: "gpay" | "phonepe" | "paytm" | "bhim" | "generic", vpa: string, payeeName: string, note: string, amount?: number) {
    const upiLink = buildUpiLink({ vpa, name: payeeName, note, amount });

    const packageMap: Record<string, string | null> = {
      gpay: "com.google.android.apps.nbu.paisa.user",
      phonepe: "com.phonepe.app",
      paytm: "net.one97.paytm",
      bhim: "in.org.npci.upiapp",
      generic: null,
    };

    const pkg = packageMap[app];

    const isAndroid = /Android/i.test(navigator.userAgent || "");
    try {
      if (isAndroid && pkg) {
        // Use Android intent to try to open specific package (works in Chrome on Android)
        const intentUrl = `intent:${upiLink}#Intent;package=${pkg};scheme=upi;end`;
        // Setting location will trigger either the app or Play Store chooser; we attempt and then fallback to upi link after short delay
        window.location.href = intentUrl;
        // fallback to generic upi link shortly after
        setTimeout(() => {
          window.location.href = upiLink;
        }, 700);
      } else {
        // Non-Android or generic: navigate to upi:// which many mobile UPI apps register
        window.location.href = upiLink;
        // If nothing happens, user can scan QR / copy VPA
      }
    } catch (err) {
      // final fallback: copy vpa and notify user
      copy(vpa, "upi-id-fallback");
      alert("Could not open UPI app automatically. UPI ID copied to clipboard — please paste it into your UPI app.");
    }
  }

  // Generic openUpi fallback (used elsewhere)
  function openUpiGeneric(vpa: string, payeeName: string, note: string, amount?: number) {
    openUpiForApp("generic", vpa, payeeName, note, amount);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-[#eef6fb] text-[#0b1320]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header / Hero */}
        <header className="mb-8">
          {/* Changed block: use header color and white text */}
          <div
            className="rounded-2xl shadow-sm p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            style={{
              background: "linear-gradient(90deg,#0a2540 0%, #0a7acfff 100%)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: "#ffffff" }}>
                Internship Enrollment Payment
              </h1>

              <p className="mt-3 text-sm max-w-xl text-justify" style={{ color: "rgba(255,255,255,0.90)" }}>
                Secure your seat by paying the registration charge. Choose UPI, PayPal or bank transfer below.
              </p>

              <p className="mt-3 text-sm max-w-xl text-justify" style={{ color: "rgba(255,255,255,0.85)" }}>
                As discussed during the interview, the internship onboarding fee must be paid within 1–2 hours of receiving
                the official acceptance email. If the payment is not completed within this time frame, the payment link will
                automatically expire, and the seat may be offered to the next shortlisted candidate.
              </p>
            </div>

            <div className="text-center sm:text-right">
              <div className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
                Amount (INR)
              </div>
              <div className="mt-1 text-2xl font-bold" style={{ color: "#ffffff" }}>
                ₹{amountINR.toLocaleString()}
              </div>
              <div className="mt-1 text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
                Or ${amountUSD} via PayPal
              </div>
            </div>
          </div>
        </header>

        {/* small info row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-lg bg-white p-4 border border-[#e6eef6] shadow-sm">
            <h3 className="font-semibold">What you're paying for</h3>
            <p className="mt-2 text-sm text-[#0b1320]/70">Registration fee for your selected internship role. Manual verification follows payment.</p>
          </div>
          <div className="rounded-lg bg-white p-4 border border-[#e6eef6] shadow-sm">
            <h3 className="font-semibold">Confirmation</h3>
            <p className="mt-2 text-sm text-[#0b1320]/70">Email payment screenshot to <strong>contact@technocolabs.com</strong>. We'll confirm within 1–2 hours same day.</p>
          </div>
          <div className="rounded-lg bg-white p-4 border border-[#e6eef6] shadow-sm">
            <h3 className="font-semibold">Security</h3>
            <p className="mt-2 text-sm text-[#0b1320]/70">We do not store your card/bank details. Use official apps and double-check payment details.</p>
          </div>
        </div>

        <main className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: summary + quick QR */}
          <aside className="lg:col-span-1">
            <div className="rounded-xl bg-white p-5 border border-[#e6eef6] shadow-sm">
              <div className="text-xs font-semibold text-[#0a66c2]">Summary</div>
              <div className="mt-4 space-y-3">
                <div>
                  <div className="text-xs text-[#0b1320]/60">Name</div>
                  <div className="font-medium">{s.name ?? "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-[#0b1320]/60">Role</div>
                  <div className="font-medium">{s.role ?? "Internship"}</div>
                </div>
                <div>
                  <div className="text-xs text-[#0b1320]/60">Amount</div>
                  <div className="font-medium">₹{amountINR.toLocaleString()} • ${amountUSD}</div>
                </div>
              </div>

              <div className="mt-4 border-t pt-3 text-sm text-[#0b1320]/60">{confirmNote}</div>
            </div>

            <div className="mt-4 rounded-xl bg-white p-4 border border-[#e6eef6] shadow-sm text-center">
              <div className="text-xs font-semibold text-[#0a66c2]">UPI Quick Pay</div>

              <div className="mt-4 flex justify-center">
                <div
                  className="rounded-lg p-2 bg-[#f7fbff] flex items-center justify-center"
                  style={{ width: showLargeLeftQR ? 260 : 220, height: showLargeLeftQR ? 260 : 220 }}
                >
                  <img
                    src={qrPath}
                    alt="UPI QR Code"
                    className="object-contain w-full h-full rounded-md"
                    onError={() => setLeftQrError(true)}
                  />
                </div>
              </div>

              {leftQrError ? (
                <div className="mt-3 text-sm text-red-600">QR not found — place <code>/QR-Code.png</code> in public folder.</div>
              ) : (
                <>
                  <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                    <button
                      onClick={() => copy(UPI_ID, "upi-id-left")}
                      className={`px-3 py-2 rounded-md text-sm ${copiedKey === "upi-id-left" ? "bg-green-700 text-white" : "bg-[#0A66C2] text-white"}`}
                    >
                      {copiedKey === "upi-id-left" ? "Copied" : "Copy UPI ID"}
                    </button>

                    <button onClick={() => setShowLargeLeftQR((v) => !v)} className="px-3 py-2 rounded-md border text-sm">
                      {showLargeLeftQR ? "Smaller QR" : "Larger QR"}
                    </button>

                    {/* NOTE: Download QR removed from left quick pay as requested */}
                  </div>

                  <div className="mt-3 text-xs text-[#0b1320]/70">
                    <div className="font-mono">{UPI_ID}</div>
                    <div className="mt-1">Scan QR or paste UPI ID in your app → Enter amount → Add note: name + role</div>
                  </div>
                </>
              )}
            </div>
          </aside>

          {/* RIGHT: payment methods */}
          <section className="lg:col-span-2 space-y-6">
            <div className="rounded-xl bg-white p-3 border border-[#e6eef6] shadow-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setSelected("paypal")}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${selected === "paypal" ? "bg-[#ffd24a] text-black" : "text-[#0b1320]"}`}
                >
                  PayPal
                </button>
                <button
                  onClick={() => setSelected("upi")}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${selected === "upi" ? "bg-[#0A66C2] text-white" : "text-[#0b1320]"}`}
                >
                  UPI
                </button>
                <button
                  onClick={() => setSelected("bank")}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${selected === "bank" ? "bg-[#0A66C2] text-white" : "text-[#0b1320]"}`}
                >
                  Bank Transfer
                </button>
                <div className="ml-auto text-xs text-[#0b1320]/60">Secure • Manual confirmation required</div>
              </div>

              {/* PAYPAL */}
              {selected === "paypal" && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                  <div className="md:col-span-2 space-y-3">
                    <div className="rounded-lg overflow-hidden" style={{ background: "#ffd24a" }}>
                      <div className="flex items-center justify-between px-4 py-5">
                        <div className="flex items-center gap-4">
                          {/* make paypal icon look white on dark bg */}
                          <img src={PAYPAL_ICON} alt="paypal" className="w-10 h-10 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
                          <div>
                            <div className="text-sm font-semibold text-[#0b1320]">Pay with PayPal</div>
                            <div className="text-xs text-[#0b1320]/70">Secure global checkout</div>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-sm text-[#0b1320]/60">Amount</div>
                          <div className="text-lg font-bold text-[#0b1320]">USD ${amountUSD}</div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-[#e6eef6] p-4 bg-white">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                        <div>
                          <div className="text-xs text-[#0b1320]/60">PayPal Email</div>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="font-medium text-[#0b1320]">{PAYPAL_EMAIL}</div>
                            <button onClick={() => copy(PAYPAL_EMAIL, "paypal-email")} className={`px-3 py-1 rounded-md text-sm ${copiedKey === "paypal-email" ? "bg-green-700 text-white" : "bg-[#0A66C2] text-white"}`}>
                              {copiedKey === "paypal-email" ? "Copied" : "Copy"}
                            </button>
                          </div>

                          <div className="mt-3 text-xs text-[#0b1320]/60">PayPal Account Name</div>
                          <div className="font-medium text-[#0b1320]">{PAYPAL_NAME}</div>

                          <div className="mt-4 text-xs text-[#0b1320]/70">Tip: Use the checkout link or send to the above email for direct payments.</div>
                        </div>

                        <div className="flex flex-col items-center gap-3">
                          <a
                            href={PAYPAL_LINK}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-2 rounded-md border text-sm inline-flex items-center gap-2 justify-center"
                            style={{ background: "#0b3d91", color: "white", borderColor: "#0b3d91" }}
                          >
                            <img
                              src={PAYPAL_ICON}
                              alt="PayPal"
                              className="w-5 h-5 object-contain"
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                            <span>Checkout</span>
                          </a>

                          <button onClick={() => copy(PAYPAL_LINK, "paypal-link")} className={`px-3 py-2 rounded-md border text-sm ${copiedKey === "paypal-link" ? "bg-green-700 text-white" : ""}`}>
                            {copiedKey === "paypal-link" ? "Copied" : "Copy Link"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-3">
                    <div className="text-xs text-[#0b1320]/60">Equivalent</div>
                    <div className="text-2xl font-bold text-[#0b1320]">₹{amountINR.toLocaleString()}</div>
                    <div className="text-xs text-[#0b1320]/60">or ${amountUSD}</div>
                  </div>
                </div>
              )}

              {/* UPI */}
              {selected === "upi" && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-[#e6eef6] p-4 bg-white">
                    <div className="text-xs text-[#0b1320]/60">UPI ID</div>
                    <div className="mt-2 font-medium text-lg text-[#0b1320]">{UPI_ID}</div>

                    <div className="mt-3 flex gap-2 flex-wrap">
                      <button onClick={() => copy(UPI_ID, "upi-id-right")} className={`px-3 py-2 rounded-lg text-sm ${copiedKey === "upi-id-right" ? "bg-green-700 text-white" : "bg-[#0A66C2] text-white"}`}>
                        {copiedKey === "upi-id-right" ? "Copied" : "Copy UPI ID"}
                      </button>
                      <button onClick={() => openUpiGeneric(UPI_ID, "TECHNOCOLABS SOFTWARE PRIVATE LIMITED", "Internship Enrollment Fees", amountINR)} className="px-3 py-2 rounded-lg border text-sm">
                        Open in UPI App
                      </button>
                    </div>

                    <div className="mt-3 text-xs text-[#0b1320]/60">Open your UPI app → Pay → Enter amount → Add note: your name + role</div>

                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      {/* Buttons try to open that specific app first (Android intent), then fallback to upi:// */}
                      <button
                        onClick={() => openUpiForApp("gpay", UPI_ID, "TECHNOCOLABS SOFTWARE PRIVATE LIMITED", "Internship Enrollment Fees", amountINR)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-[#e6eef6]"
                        title="Open Google Pay"
                      >
                        <img src={ICON_GPAY} alt="GPay" className="w-6 h-6" />
                      </button>

                      <button
                        onClick={() => openUpiForApp("phonepe", UPI_ID, "TECHNOCOLABS SOFTWARE PRIVATE LIMITED", "Internship Enrollment Fees", amountINR)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-[#e6eef6]"
                        title="Open PhonePe"
                      >
                        <img src={ICON_PHONEPE} alt="PhonePe" className="w-6 h-6" />
                      </button>

                      <button
                        onClick={() => openUpiForApp("paytm", UPI_ID, "TECHNOCOLABS SOFTWARE PRIVATE LIMITED", "Internship Enrollment Fees", amountINR)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-[#e6eef6]"
                        title="Open Paytm"
                      >
                        <img src={ICON_PAYTM} alt="Paytm" className="w-6 h-6" />
                      </button>

                      <button
                        onClick={() => openUpiForApp("generic", UPI_ID, "TECHNOCOLABS SOFTWARE PRIVATE LIMITED", "Internship Enrollment Fees", amountINR)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-[#e6eef6]"
                        title="Open CRED or other UPI app"
                      >
                        <img src={ICON_CRED} alt="CRED" className="w-6 h-6 object-contain" />
                      </button>

                      {/* BHIM */}
                      <button
                        onClick={() => openUpiForApp("bhim", UPI_ID, "TECHNOCOLABS SOFTWARE PRIVATE LIMITED", "Internship Enrollment Fees", amountINR)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-[#e6eef6]"
                        title="Open BHIM"
                      >
                        <img src={ICON_BHIM} alt="BHIM" className="w-6 h-6" />
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg flex flex-col items-center justify-center p-4 border border-[#e6eef6] bg-white">
                    <img
                      src={qrPath}
                      alt="UPI QR Code"
                      className="w-64 h-64 object-contain rounded-md"
                      onError={() => setRightQrError(true)}
                    />
                    {rightQrError && <div className="mt-2 text-sm text-red-600">QR not found at {qrPath}</div>}
                    <div className="mt-3 text-sm text-[#0b1320]/70 text-center">Scan to pay via UPI</div>
                    <a href={qrPath} download className="mt-3 px-3 py-2 rounded-md bg-[#0A66C2] text-white text-sm">Download QR</a>
                  </div>
                </div>
              )}

              {/* BANK */}
              {selected === "bank" && (
                <div className="mt-4 rounded-lg border border-[#e6eef6] p-4 bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-[#0b1320]/60">Bank</div>
                      <div className="font-medium text-[#0b1320]">{BANK.name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#0b1320]/60">Account Name</div>
                      <div className="font-medium text-[#0b1320]">{BANK.accountName}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#0b1320]/60">Account Number</div>
                      <div className="font-medium text-[#0b1320]">{BANK.accountNo}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#0b1320]/60">IFSC</div>
                      <div className="font-medium text-[#0b1320]">{BANK.ifsc}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#0b1320]/60">Account Type</div>
                      <div className="font-medium text-[#0b1320]">{BANK.type}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={() => copy(BANK.accountNo, "bank-ac")} className={`px-3 py-2 rounded-md border text-sm ${copiedKey === "bank-ac" ? "bg-green-700 text-white" : ""}`}>
                      {copiedKey === "bank-ac" ? "Copied" : "Copy A/C No"}
                    </button>
                    <button onClick={() => copy(BANK.ifsc, "bank-ifsc")} className={`px-3 py-2 rounded-md border text-sm ${copiedKey === "bank-ifsc" ? "bg-green-700 text-white" : ""}`}>
                      {copiedKey === "bank-ifsc" ? "Copied" : "Copy IFSC"}
                    </button>
                    <button onClick={copyAllBank} className={`px-3 py-2 rounded-md bg-[#0A66C2] text-white text-sm ${copiedKey === "bank-all" ? "opacity-80" : ""}`}>
                      {copiedKey === "bank-all" ? "Copied" : "Copy All Bank Details"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* After you pay */}
            <div className="rounded-xl bg-white p-4 border border-[#e6eef6] shadow-sm">
              <h4 className="text-sm font-semibold text-[#0b1320]">After you pay</h4>
              <ol className="mt-2 list-decimal pl-5 text-sm text-[#0b1320]/70 space-y-2">
                <li>Take a screenshot of the payment confirmation/receipt.</li>
                <li>Email it to <strong>contact@technocolabs.com</strong> with your name and role (or reply to the registration email).</li>
                <li>We’ll confirm receipt and next steps within 1–2 business days.</li>
              </ol>
            </div>

            {/* Payment terms */}
            <section className="rounded-xl bg-white p-4 border border-[#e6eef6] shadow-sm">
              <h4 className="text-sm font-semibold text-[#0b1320]">Payment Terms &amp; Conditions</h4>
              <div className="mt-3 space-y-3 text-sm text-[#0b1320]/70">
                <div>
                  <strong className="block">1. Payment Policy</strong>
                  <div>All payments made on our website are final and processed through secure payment gateways.</div>
                </div>
                <div>
                  <strong className="block">2. Fees &amp; Taxes</strong>
                  <div>Displayed fees include applicable taxes and processing charges.</div>
                </div>
                <div>
                  <strong className="block">3. Confirmation</strong>
                  <div>You will receive an enrollment confirmation email within <strong>1–2 hours</strong> after a successful payment.</div>
                </div>
                <div>
                  <strong className="block">4. Refunds</strong>
                  <div>Payments are <strong>non-refundable</strong>. Refunds are only issued for verified duplicate or failed transactions.</div>
                </div>
                <div>
                  <strong className="block">5. Payment Issues</strong>
                  <div>If money is deducted but payment fails, it is usually reversed by your bank within <strong>5–7 working days</strong>.</div>
                </div>
                <div>
                  <strong className="block">6. Security</strong>
                  <div>We do not store your card/bank details. All transactions are encrypted and handled by certified gateways.</div>
                </div>
                <div>
                  <strong className="block">7. Changes</strong>
                  <div>We may update these terms anytime without prior notice.</div>
                </div>

                <div className="pt-2">
                  <strong>8. For Support Contact Us:</strong>{" "}
                  <a className="text-[#0A66C2] underline" href="mailto:contact@technocolabs.com">contact@technocolabs.com</a>{" "}
                  <span className="mx-1">|</span>
                  <a className="text-[#0A66C2] underline" href="mailto:technocollabs@gmail.com">technocollabs@gmail.com</a>
                </div>
                <div className="pt-2">
                  <strong>9. Call Us:</strong>{" "}
                  <a className="text-[#0A66C2] underline" href="tel:+918319291391">+91 8319291391</a>
                </div>
              </div>
            </section>
          </section>
        </main>
      </div>
    </div>
  );
}
